import { type CSSProperties, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CalendarCheck, CaretRight, Clock, InstagramLogo, MapPin, Phone, TelegramLogo, WhatsappLogo } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { duration, fmt, humanDate, localDate, phoneHref, plural, priceLabel, shortAddress, whatsappHref } from '@/lib/format';
import { hoursSummary, openStatus } from '@/lib/hours';
import type { Service } from '@/lib/types';
import { InfoIcon, Photo } from '@/components/ui';
import { useSlug, useStudio } from '@/features/studio/StudioRoot';
import { useNextBooking, useUpcomingCount } from '@/features/studio/StudioLayout';
import { availabilityKey } from '@/features/booking/SlotPicker';
import { BrightBooking } from './BrightBooking';
import { serviceLabels } from './labels';

/** The bright storefront: one scrolling page, booking opens in a sheet over it (`?book=1`). */
export function BrightHome() {
  const sf = useStudio();
  const { tenant, services, resources } = sf;
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const status = useMemo(() => openStatus(sf), [sf]);
  const labels = useMemo(() => serviceLabels(services), [services]);
  const categories = useMemo(() => [...new Set(services.map((s) => s.category || 'Услуги'))], [services]);
  const [category, setCategory] = useState(categories[0] ?? '');
  const [expanded, setExpanded] = useState(false);
  const inCategory = services.filter((s) => categories.length < 2 || (s.category || 'Услуги') === category);
  // Long categories show the first rows; the rest open in place.
  const shown = expanded || inCategory.length <= 8 ? inCategory : inCategory.slice(0, 6);
  const minPrice = services.filter((s) => s.price > 0).reduce((m, s) => Math.min(m, s.price), Infinity);
  const popular = useMemo(() => {
    const p = services.filter((s) => s.popular);
    return (p.length ? p : services).slice(0, 4);
  }, [services]);

  const isOpen = params.has('book');
  // Opening pushes a history entry so the phone's Back button closes the sheet.
  const open = (serviceId?: string, start?: string) => {
    const p = new URLSearchParams({ book: '1' });
    if (serviceId) p.set('service', serviceId);
    if (start) p.set('start', start);
    setParams(p, { state: { sheet: true } });
  };
  const close = () => {
    if ((location.state as { sheet?: boolean } | null)?.sheet) navigate(-1);
    else setParams({}, { replace: true });
  };

  const instagram = tenant.instagram.replace(/^@/, '');
  const price = (s: Service) => priceLabel(s.price, tenant.currency, s.price_is_from);

  return (
    <>
      <header className="br-hero" data-photo={tenant.hero_url ? '' : undefined}>
        {tenant.hero_url && <Photo src={tenant.hero_url} alt="" className="br-hero__photo" eager />}
        <span className="br-hero__top">
          <span className="br-pill" data-open={status.open}>
            <i aria-hidden />
            {status.text}
          </span>
          <span className="br-hero__btns">
            {instagram && (
              <a href={`https://instagram.com/${instagram}`} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <InstagramLogo size={19} weight="bold" aria-hidden />
              </a>
            )}
            {tenant.phone && (
              <a href={phoneHref(tenant.phone)} aria-label={`Позвонить: ${tenant.phone}`}>
                <Phone size={18} weight="bold" aria-hidden />
              </a>
            )}
          </span>
        </span>
        <span className="br-hero__body">
          <h1 className="br-hero__name" style={{ '--name-len': Math.max(...tenant.name.split(/\s+/).map((w) => w.length)) } as CSSProperties}>
            {tenant.name}
          </h1>
          {tenant.tagline && <p className="br-hero__tag">{tenant.tagline}</p>}
          <span className="br-hero__meta">
            {Number.isFinite(minPrice) && <span>от {priceLabel(minPrice, tenant.currency)}</span>}
            {resources.length > 0 && (
              <span>
                {resources.length} {plural(resources.length, 'мастер', 'мастера', 'мастеров')}
              </span>
            )}
            {tenant.address &&
              (tenant.map_url ? (
                <a href={tenant.map_url} target="_blank" rel="noopener noreferrer" aria-label={`Адрес на карте: ${tenant.address}`}>
                  <MapPin size={16} aria-hidden />
                  {shortAddress(tenant.address)}
                </a>
              ) : (
                <span>
                  <MapPin size={16} aria-hidden />
                  {shortAddress(tenant.address)}
                </span>
              ))}
          </span>
        </span>
      </header>

      <HeroCard service={popular[0]} onBook={open} />

      <section className="br-sec" aria-labelledby="br-popular">
        <h2 className="br-h2" id="br-popular">
          Популярное
        </h2>
        <ul className="br-tiles">
          {popular.map((s) => (
            <li key={s.id}>
              <button type="button" className="br-tile" onClick={() => open(s.id)}>
                {labels.get(s.id)?.category && <small className="br-tile__tag">{labels.get(s.id)!.category}</small>}
                <b>{s.name}</b>
                {s.description && <span className="br-tile__desc">{s.description}</span>}
                <span className="br-tile__foot">
                  <span>{duration(s.duration_min)}</span>
                  <strong>{price(s)}</strong>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="br-sec" aria-labelledby="br-price">
        <h2 className="br-h2" id="br-price">
          Прайс <small>{services.length} {plural(services.length, 'услуга', 'услуги', 'услуг')}</small>
        </h2>
        {categories.length > 1 && (
          <span className="br-tabs" role="group" aria-label="Категории">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                className="br-tab"
                aria-pressed={c === category}
                onClick={() => {
                  setCategory(c);
                  setExpanded(false);
                }}
              >
                {c}
              </button>
            ))}
          </span>
        )}
        <ul className="br-list br-list--cards">
          {shown.map((s) => (
            <li key={s.id}>
              <button type="button" className="br-row" onClick={() => open(s.id)}>
                <span>
                  <b>{s.name}</b>
                  {s.description && <span className="br-row__desc">{s.description}</span>}
                  <small>{duration(s.duration_min)}</small>
                </span>
                <span className="br-row__price">{price(s)}</span>
              </button>
            </li>
          ))}
        </ul>
        {shown.length < inCategory.length && (
          <button type="button" className="br-more" onClick={() => setExpanded(true)}>
            Ещё {inCategory.length - shown.length} {plural(inCategory.length - shown.length, 'услуга', 'услуги', 'услуг')}
          </button>
        )}
      </section>

      {tenant.info_cards.length > 0 && (
        <section className="br-sec" aria-labelledby="br-about">
          <h2 className="br-h2" id="br-about">
            О студии
          </h2>
          <ul className="br-cards">
            {tenant.info_cards.map((c, i) => (
              <li key={i} className="br-card">
                <span className="br-card__icon">
                  <InfoIcon name={c.icon} size={24} />
                </span>
                <b>{c.title}</b>
                <p>{c.text}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="br-sec" aria-labelledby="br-find">
        <h2 className="br-h2" id="br-find">
          Как нас найти
        </h2>
        <ul className="br-card br-info">
          {(tenant.address || tenant.address_note) && (
            <li>
              <MapPin size={19} aria-hidden />
              {tenant.map_url ? (
                <a href={tenant.map_url} target="_blank" rel="noopener noreferrer">
                  {[tenant.address, tenant.address_note].filter(Boolean).join('. ')}
                </a>
              ) : (
                <span>{[tenant.address, tenant.address_note].filter(Boolean).join('. ')}</span>
              )}
            </li>
          )}
          <li>
            <Clock size={19} aria-hidden />
            <span>{hoursSummary(sf)}</span>
          </li>
          {tenant.phone && (
            <li>
              <Phone size={19} aria-hidden />
              <a href={phoneHref(tenant.phone)}>{tenant.phone}</a>
            </li>
          )}
          {instagram && (
            <li>
              <InstagramLogo size={19} aria-hidden />
              <a href={`https://instagram.com/${instagram}`} target="_blank" rel="noopener noreferrer">
                @{instagram}
              </a>
            </li>
          )}
          {tenant.telegram && (
            <li>
              <TelegramLogo size={19} aria-hidden />
              <a href={`https://t.me/${tenant.telegram.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer">
                Telegram
              </a>
            </li>
          )}
        </ul>
      </section>

      <Footer />

      <nav className="br-bar" aria-label="Запись">
        <button type="button" className="br-btn" onClick={() => open()}>
          Записаться онлайн
        </button>
        {tenant.whatsapp ? (
          <a className="br-wa" href={whatsappHref(tenant.whatsapp, 'Здравствуйте! Хочу записаться')} target="_blank" rel="noopener noreferrer" aria-label="Написать в WhatsApp">
            <WhatsappLogo size={24} aria-hidden />
          </a>
        ) : (
          tenant.telegram && (
            <a className="br-wa" href={`https://t.me/${tenant.telegram.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" aria-label="Написать в Telegram">
              <TelegramLogo size={24} aria-hidden />
            </a>
          )
        )}
      </nav>

      <BrightBooking
        isOpen={isOpen}
        onClose={close}
        serviceId={params.get('service')}
        masterId={params.get('master')}
        start={params.get('start')}
      />
    </>
  );
}

/**
 * The hero's action card: the client's own upcoming visit (until the studio cancels it), otherwise
 * the nearest free times for the top service — one tap opens the sheet with that time picked.
 */
function HeroCard({ service, onBook }: { service?: Service; onBook: (serviceId?: string, start?: string) => void }) {
  const slug = useSlug();
  const { tenant } = useStudio();
  const tz = tenant.timezone;
  const next = useNextBooking(slug);
  const booking = useQuery({ queryKey: ['booking', next?.token], queryFn: () => api.booking(next!.token), enabled: !!next, staleTime: 60_000 });
  const today = localDate(new Date(), tz);
  const slots = useQuery({
    queryKey: availabilityKey(slug, service?.id ?? '', null, today, 7),
    queryFn: () => api.availability(slug, service!.id, null, today, 7),
    enabled: !!service,
    staleTime: 20_000,
  });

  if (next && booking.data?.status !== 'cancelled') {
    return (
      <Link className="br-hcard br-hcard--mine" to={`/s/${slug}/me?b=${next.id}`}>
        <CalendarCheck size={26} weight="fill" aria-hidden />
        <span>
          <small>Вы записаны</small>
          <b>
            {humanDate(localDate(new Date(next.startsAt), tz), tz)}, {fmt(next.startsAt, tz, 'HH:mm')}
          </b>
          <small>{next.serviceName}</small>
        </span>
        <CaretRight size={18} weight="bold" aria-hidden />
      </Link>
    );
  }

  const day = slots.data?.days.find((d) => d.slots.some((s) => s.free));
  // One time per hour reads as a real choice; 12:15, 12:30, 12:45 looks like one option.
  const times = (day?.slots ?? []).filter((s, i, all) => s.free && all.findIndex((o) => o.free && fmt(o.start, tz, 'HH') === fmt(s.start, tz, 'HH')) === i).slice(0, 3);
  if (!service || slots.isError || (slots.isSuccess && !day)) {
    return (
      <button type="button" className="br-hcard br-hcard--mine" onClick={() => onBook()}>
        <CalendarCheck size={26} weight="fill" aria-hidden />
        <span>
          <small>Онлайн-запись</small>
          <b>Выбрать время</b>
          <small>Без звонков и переписки</small>
        </span>
        <CaretRight size={18} weight="bold" aria-hidden />
      </button>
    );
  }
  return (
    <section className="br-hcard" aria-label="Ближайшее свободное время">
      <small>Ближайшее свободное время</small>
      <span className="br-hcard__head">
        <b data-loading={slots.isPending || undefined}>{day ? humanDate(day.date, tz) : 'Ищем окна'}</b>
        <span>{service.name}</span>
      </span>
      <span className="br-hcard__times">
        {slots.isPending
          ? [0, 1, 2].map((i) => <span key={i} className="br-hcard__time" data-loading />)
          : times.map((s) => (
              <button key={s.start} type="button" className="br-hcard__time" onClick={() => onBook(service.id, s.start)}>
                {fmt(s.start, tz, 'HH:mm')}
              </button>
            ))}
        <button type="button" className="br-hcard__time" data-more onClick={() => onBook(service.id)} aria-label="Другое время">
          <ArrowRight size={20} weight="bold" aria-hidden />
        </button>
      </span>
    </section>
  );
}

function Footer() {
  const slug = useSlug();
  const { tenant } = useStudio();
  const upcoming = useUpcomingCount(slug);
  return (
    <footer className="br-foot">
      © {new Date().getFullYear()} {tenant.name}
      <span>
        {upcoming > 0 && <Link to={`/s/${slug}/me`}>Мои записи</Link>}
        <Link to={`/s/${slug}/owner`}>Кабинет студии</Link>
      </span>
    </footer>
  );
}

/** `/book?service=…` links (services page, booking links) open the sheet on the bright home. */
export function BrightBookRedirect() {
  const slug = useSlug();
  const [params] = useSearchParams();
  const p = new URLSearchParams({ book: '1' });
  for (const k of ['service', 'master', 'start']) {
    const v = params.get(k);
    if (v) p.set(k, v);
  }
  return <Navigate to={`/s/${slug}?${p}`} replace />;
}
